import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { PencilSimple, Plus, Wrench, Trash, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';
import { mfgApi, EQ_STATUS_BADGE, EQ_KIND_BADGE } from './mfgApi';
import { useDepartment } from './DepartmentContext';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildEquipmentLabel } from '@/components/labels/labelSizes';
import BarcodeLabel from './BarcodeLabel';
import ScheduleFormDialog from './ScheduleFormDialog';
import CompleteScheduleDialog from './CompleteScheduleDialog';
import CorrectiveDialog from './CorrectiveDialog';
import { ReplacementPartButton } from '@/components/registration';
import { ItemLifecycleActions } from '@/components/lifecycle/ItemLifecycleActions';
import EquipmentDevelopmentSection from './EquipmentDevelopmentSection';

export default function EquipmentDetailDialog({ equipmentId, onClose, onChanged, onEdit }) {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const isAdmin = user?.role === 'admin';
  const [eq, setEq] = useState(null);
  const [schedDialog, setSchedDialog] = useState({ open: false, schedule: null });
  const [completeFor, setCompleteFor] = useState(null);
  const [corrOpen, setCorrOpen] = useState(false);
  const [labelData, setLabelData] = useState(null);
  // 2026-10-23 · 廃棄（理由必須）／ 廃棄を取り消す ／ 削除（登録ミス）
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ accepted_on: '', accepted_by_name: '', memo: '' });
  const [disposeReason, setDisposeReason] = useState('');
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState('');
  // 2026-11-05 · 履歴があっても削除する（管理者だけ・記録は残す）
  const [delForce, setDelForce] = useState(false);
  const [acting, setActing] = useState(false);
  const { active } = useDepartment();
  const deptName = active?.name;

  const load = useCallback(() => {
    if (!equipmentId) return;
    mfgApi.equipmentOne(equipmentId).then(setEq).catch(() => {});
  }, [equipmentId]);
  useEffect(() => { load(); }, [load]);

  if (!equipmentId) return null;

  const changeStatus = async (status) => {
    if (status === 'retired') { setDisposeReason(''); setDisposeOpen(true); return; }
    try { await mfgApi.setEquipmentStatus(equipmentId, { status }); toast.success(t('mfg.common.saved')); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };
  const submitAcceptance = async () => {
    try {
      const r = await mfgApi.confirmAcceptance(equipmentId, {
        accepted_on: acceptForm.accepted_on || undefined,
        accepted_by_name: acceptForm.accepted_by_name || undefined,
        memo: acceptForm.memo || undefined,
      });
      setAcceptOpen(false);
      load(); onChanged?.();
      toast.success(`受入確認を記録しました${r.record_version ? `（完了報告書 v${r.record_version}）` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || '受入確認を記録できませんでした');
    }
  };

  const dispose = async () => {
    if (!disposeReason.trim()) { toast.error('廃棄の理由を入力してください'); return; }
    setActing(true);
    try {
      await mfgApi.setEquipmentStatus(equipmentId, { status: 'retired', reason: disposeReason.trim() });
      toast.success('廃棄しました（履歴に残ります）');
      setDisposeOpen(false); load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setActing(false); }
  };
  const undispose = async () => {
    if (!window.confirm('廃棄を取り消して、前の状態に戻します。よろしいですか？')) return;
    try {
      await mfgApi.undisposeEquipment(equipmentId, '登録ミスのため');
      toast.success('廃棄を取り消しました');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };
  const softDelete = async () => {
    if (!delReason.trim()) { toast.error('削除の理由を入力してください'); return; }
    setActing(true);
    try {
      await mfgApi.softDeleteEquipment(equipmentId, delReason.trim(), delForce);
      toast.success('削除しました（設定 → データ点検 から元に戻せます）');
      setDelOpen(false); onChanged?.(); onClose();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === 'object'
        ? `${det.message} ${(det.unmet || []).map((u) => u.label).join('、')}`
        : (det || t('mfg.common.error')));
    } finally { setActing(false); }
  };
  const delSchedule = async (s) => {
    if (!window.confirm(`${s.title} を削除しますか？`)) return;
    try { await mfgApi.deleteSchedule(s.id); toast.success(t('mfg.common.deleted')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };
  const recordCheck = async (kindKey) => {
    try {
      await mfgApi.equipmentCheck(equipmentId, { type: kindKey });
      toast.success(t('mfg.common.saved'));
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };
  const closeLog = async (l) => {    try { await mfgApi.updateLog(l.id, { status: 'closed', restore_operational: true }); toast.success(t('mfg.common.saved')); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };

  // R2 · 点検 / 校正 / 予防保全 をひとつの表にまとめる
  const maintRows = !eq ? [] : [
    ...[['inspection', '点検'], ['calibration', '校正']].flatMap(([k, typeLabel]) => {
      const due = eq[`${k}_due_on`];
      if (k === 'calibration' && !eq.calibration_required && !due) return [];
      const months = eq[`${k}_interval_months`];
      const days = eq[`${k}_interval_days`];
      return [{
        key: k, typeLabel, title: typeLabel, due, last: eq[`last_${k}_on`],
        interval: months ? `${months}か月` : (days ? `${days}日` : ''),
        owner: eq[`${k}_owner`], overdue: eq[`${k}_overdue`], soon: eq[`${k}_due_soon`],
        onDone: () => recordCheck(k), schedule: null,
      }];
    }),
    ...(eq.schedules || []).map((sc) => ({
      key: sc.id, typeLabel: '予防保全',
      title: `${sc.title}${sc.active === false ? '（無効）' : ''}`,
      due: sc.next_due_on, last: sc.last_done_on, owner: sc.owner || sc.assignee,
      interval: sc.interval_months ? `${sc.interval_months}か月`
        : (sc.interval_days ? `${sc.interval_days}日` : ''),
      overdue: sc.overdue, soon: sc.due_soon,
      onDone: () => setCompleteFor(sc), schedule: sc,
    })),
  ];

  const info = (label, val) => (
    <div><p className="text-[11px] text-zinc-400">{label}</p><p className="text-zinc-800">{val || '—'}</p></div>
  );

  return (
    <Dialog open={!!equipmentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="equipment-detail-dialog">
        {!eq ? (
          <><DialogHeader><DialogTitle className="sr-only">{t('mfg.eq.title')}</DialogTitle></DialogHeader><div className="py-16 text-center text-sm text-zinc-400">…</div></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 flex-wrap pr-8">
                <span className="font-mono text-sm text-zinc-500">{eq.asset_no}</span>
                <span>{eq.name}</span>
                <Badge variant="outline" className={EQ_KIND_BADGE[eq.kind]}>{eq.kind_label}</Badge>
                <Badge variant="outline" className={EQ_STATUS_BADGE[eq.status]}>{eq.status_label}</Badge>
                {/* 2026-10-21 · 由来（既存 / 購入 / 自社製作）のタグ */}
                <Badge variant="outline" data-testid="eq-origin-tag"
                  className={eq.acquisition_type === 'in_house'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : eq.acquisition_type === 'purchase'
                      ? 'bg-sky-50 text-sky-700 border-sky-200'
                      : 'bg-zinc-100 text-zinc-700 border-zinc-200'}>
                  {eq.acquisition_type === 'in_house' ? '自社製作（技術部）'
                    : eq.acquisition_type === 'purchase' ? '購入' : '既存'}
                </Badge>
                {/* 6 · 受入確認（引き渡しのサインオフ・2026-11-04） */}
                {eq.source_development_id && (
                  eq.acceptance_confirmed_on ? (
                    <Badge variant="outline" data-testid="eq-acceptance-badge"
                      className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      受入確認済み {eq.acceptance_confirmed_on} {eq.acceptance_confirmed_by_name || ''}
                    </Badge>
                  ) : (
                    <Badge variant="outline" data-testid="eq-acceptance-badge"
                      className="bg-amber-50 text-amber-800 border-amber-200">未確認</Badge>
                  )
                )}
                {canWrite && eq.source_development_id && !eq.acceptance_confirmed_on && (
                  <Button size="sm" variant="outline" className="h-8"
                    onClick={() => setAcceptOpen(true)} data-testid="eq-acceptance-btn">
                    <CheckCircle size={14} className="mr-1" /> 受入確認
                  </Button>
                )}
                {canWrite && (
                  <Button size="sm" variant="outline" data-testid="equipment-edit-btn"
                    onClick={() => onEdit?.(eq)} className="ml-auto h-8">
                    <PencilSimple size={14} className="mr-1" /> {t('mfg.common.edit')}
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {info(t('mfg.eq.maker'), eq.maker)}
              {info(t('mfg.eq.model'), eq.model)}
              {info(t('mfg.eq.serial'), eq.serial)}
              {info(t('mfg.eq.location'), eq.location_label || eq.location_name)}
              {info(t('mfg.eq.installedOn'), eq.installed_on)}
              {/* 2026-10-22 · 取得日（未入力の古い行は導入日を表示） */}
              {info('取得日', eq.acquired_on)}
              {info(t('mfg.eq.owner'), eq.owner)}
            </div>
            {eq.notes && <p className="text-sm text-zinc-600">{eq.notes}</p>}

            {/* R2 · ひとつの操作の列 — 状態を変更・移動・修理/メンテを記録・交換部品を依頼 */}
            <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3" data-testid="eq-action-row">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-zinc-500">{t('mfg.eq.changeStatus')}</span>
                <select value={eq.status || ''} disabled={!canWrite || eq.status === 'retired'}
                  onChange={(e) => changeStatus(e.target.value)}
                  className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs text-zinc-800 disabled:opacity-60"
                  data-testid="eq-status-select">
                  {Object.entries(eq.statuses || {}).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                {eq.status === 'retired' && (
                  <span className="text-[11px] text-rose-700" data-testid="eq-disposed-note">
                    廃棄済み{eq.disposed_reason ? `（${eq.disposed_reason}）` : ''}
                  </span>
                )}
                {isAdmin && eq.can_undispose && (
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                    onClick={undispose} data-testid="eq-undispose-btn">
                    廃棄を取り消す
                  </Button>
                )}
                {canWrite && (
                  <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="eq-add-corrective"
                    onClick={() => setCorrOpen(true)}>
                    <Wrench size={13} className="mr-1" /> 修理・メンテを記録
                  </Button>
                )}
                <ReplacementPartButton originType="equipment" originId={eq.id}
                  originLabel={`${eq.asset_no || ''} ${eq.name || ''}`.trim()}
                  testId="mfg-equip-replacement" />
                {/* 2026-10-21 · 状態はこの上のドロップダウン ひとつだけ。
                    移動 と 廃棄（理由は必須・履歴に残る）も同じ列に並べる。 */}
                <ItemLifecycleActions register="equip" itemId={eq.id} hideHistory hideStatus
                  onlyActions={['move']}
                  onChanged={() => { load(); onChanged?.(); }} testId="mfg-equip-lifecycle" />
                {isAdmin && (
                  <Button size="sm" variant="ghost" className="ml-auto h-8 text-xs text-rose-600"
                    onClick={() => { setDelReason(''); setDelForce(false); setDelOpen(true); }}
                    data-testid="eq-soft-delete-btn">
                    <Trash size={13} className="mr-1" /> 削除（登録ミス）
                  </Button>
                )}
              </div>
            </div>

            {/* B2 (仕様 2.5) · この設備を品目に指定した 部品受注入力 の履歴（読み取り専用） */}
            {(eq.requests || []).length > 0 && (
              <section className="rounded-lg border border-zinc-200 p-3" data-testid="eq-req-history">
                <h3 className="text-sm font-semibold text-zinc-700 mb-2">{t('reg.reqHistory')}</h3>
                <ul className="divide-y divide-zinc-100 text-[12px]">
                  {eq.requests.map((r) => (
                    <li key={`${r.request_id}-${r.line_index}`} className="flex items-center gap-2 py-1.5"
                      data-testid={`eq-req-${r.request_id}-${r.line_index}`}>
                      <span className="font-mono text-zinc-600">{r.request_no || '—'}</span>
                      <span className="truncate text-zinc-700">{r.order_name || r.name || ''}</span>
                      <span className="ml-auto text-zinc-500">{r.qty} {r.uom || ''}</span>
                      <span className="text-zinc-400">{(r.created_at || '').slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* B2 · 貸出（技術の設備台帳から移した項目。一つの台帳の上で動く） */}
            {eq.active_rental && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
                data-testid="eq-active-rental">
                {t('reg.onLoanTo', { name: eq.active_rental.requested_by_name || '—',
                  due: eq.active_rental.expected_return_date || '—' })}
              </p>
            )}

            {/* 2026-10-22 · 元の開発案件 と 予備部品（空なら「なし」） */}
            <EquipmentDevelopmentSection eq={eq} canWrite={canWrite}
              onChanged={() => { load(); onChanged?.(); }} />

            {/* R2 · ひとつの「点検・保全」 */}
            <section className="rounded-lg border border-zinc-200" data-testid="eq-maint-panel">
              <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                <h3 className="text-sm font-semibold text-zinc-700">点検・保全</h3>
                {canWrite && (
                  <Button size="sm" variant="ghost" data-testid="eq-add-schedule"
                    onClick={() => setSchedDialog({ open: true, schedule: null })} className="h-7 text-orange-600">
                    <Plus size={14} className="mr-1" /> 予定を追加
                  </Button>
                )}
              </div>
              {maintRows.length === 0 ? (
                <p className="px-3 py-6 text-center text-xs text-zinc-400" data-testid="eq-maint-empty">
                  点検・校正・予防保全の予定はまだありません
                </p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {maintRows.map((m) => (
                    <li key={m.key} className="flex flex-wrap items-center gap-2 px-3 py-2"
                      data-testid={`eq-maint-${m.key}`}>
                      <Badge variant="outline" className="bg-zinc-50 text-[11px] text-zinc-700">{m.typeLabel}</Badge>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12px] font-medium text-zinc-800">{m.title}</p>
                        <p className="text-[11px] text-zinc-500 tabular-nums">
                          間隔: {m.interval || '—'} ／ 前回: {m.last || '—'} ／ 次回:{' '}
                          <span className={m.overdue ? 'font-semibold text-rose-600' : (m.soon ? 'font-semibold text-amber-600' : 'text-zinc-700')}
                            data-testid={`eq-maint-due-${m.key}`}>{m.due || '—'}</span>
                          {m.owner ? ` ／ 担当: ${m.owner}` : ''}
                        </p>
                      </div>
                      {m.overdue && (
                        <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700"
                          data-testid={`eq-maint-overdue-${m.key}`}>{t('mfg.eq.badge.overdue')}</Badge>
                      )}
                      {!m.overdue && m.soon && (
                        <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{t('mfg.eq.badge.dueSoon')}</Badge>
                      )}
                      {canWrite && m.onDone && (
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          data-testid={`eq-maint-done-${m.key}`} onClick={m.onDone}>
                          <CheckCircle size={13} className="mr-1" /> 実施を記録
                        </Button>
                      )}
                      {canWrite && m.schedule && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-zinc-500"
                            onClick={() => setSchedDialog({ open: true, schedule: m.schedule })}>
                            <PencilSimple size={14} />
                          </Button>
                          {canDeleteMfg && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600"
                              onClick={() => delSchedule(m.schedule)}><Trash size={14} /></Button>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Logs */}
            <section className="rounded-lg border border-zinc-200">
              <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                <h3 className="text-sm font-semibold text-zinc-700">{t('mfg.maint.logs')}</h3>
                {canWrite && <Button size="sm" variant="ghost" data-testid="eq-add-corrective" onClick={() => setCorrOpen(true)} className="h-7 text-orange-600"><Wrench size={14} className="mr-1" /> {t('mfg.maint.newCorrective')}</Button>}
              </div>
              {(eq.logs || []).length === 0 ? <p className="px-3 py-6 text-center text-xs text-zinc-400">{t('mfg.maint.noLogs')}</p> : (
                <ul className="divide-y divide-zinc-200">
                  {eq.logs.map((l) => (
                    <li key={l.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <Badge variant="outline" className={l.type === 'preventive' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-rose-50 text-rose-700 border-rose-200'}>{l.type_label}</Badge>
                      <div className="min-w-0 flex-1">
                        <p className="text-zinc-800 truncate">{l.action || l.symptom || l.schedule_title || '—'}</p>
                        <p className="text-[11px] text-zinc-400">{l.performed_on}{l.performed_by ? ` · ${l.performed_by}` : ''}</p>
                      </div>
                      {l.type === 'corrective' && (l.status === 'open'
                        ? (canWrite ? <Button size="sm" variant="outline" data-testid={`log-close-${l.id}`} onClick={() => closeLog(l)} className="h-7 text-xs">{t('mfg.maint.close')}</Button> : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{t('mfg.maint.statusOpen')}</Badge>)
                        : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{t('mfg.maint.statusClosed')}</Badge>)}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* R2 · ひとつの履歴（状態・移動・修理/メンテ・部品依頼・番号の変更） */}
            <section className="rounded-lg border border-zinc-200" data-testid="eq-history-panel">
              <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                <h3 className="text-sm font-semibold text-zinc-700">履歴</h3>
                <span className="text-[11px] text-zinc-400">{(eq.history || []).length}件</span>
              </div>
              {(eq.history || []).length === 0 ? (
                <p className="px-3 py-6 text-center text-xs text-zinc-400" data-testid="eq-history-empty">
                  まだ履歴はありません
                </p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {(eq.history || []).map((h, i) => (
                    <li key={`${h.kind}-${i}`} className="flex flex-wrap items-baseline gap-x-2 px-3 py-1.5 text-[11px]"
                      data-testid={`eq-history-row-${i}`}>
                      <span className="font-semibold text-zinc-800">{h.label}</span>
                      {h.detail && <span className="text-zinc-600">{h.detail}</span>}
                      <span className="ml-auto text-zinc-400">
                        {(h.at || '').slice(0, 16).replace('T', ' ')}{h.by ? ` · ${h.by}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div>
              <p className="text-xs font-semibold text-zinc-500 mb-2">{t('mfg.eq.label')}</p>
              <BarcodeLabel value={eq.asset_no} name={eq.name} sku={eq.asset_no}
                onPrint={() => setLabelData([buildEquipmentLabel(eq, deptName)])} />
            </div>
          </>
        )}
      </DialogContent>

      <ScheduleFormDialog open={schedDialog.open} schedule={schedDialog.schedule} equipmentId={equipmentId}
        onClose={() => setSchedDialog({ open: false, schedule: null })}
        onSaved={() => { setSchedDialog({ open: false, schedule: null }); load(); onChanged?.(); }} />
      <CompleteScheduleDialog open={!!completeFor} schedule={completeFor}
        onClose={() => setCompleteFor(null)} onDone={() => { setCompleteFor(null); load(); onChanged?.(); }} />
      <CorrectiveDialog open={corrOpen} equipmentId={equipmentId}
        onClose={() => setCorrOpen(false)} onDone={() => { setCorrOpen(false); load(); onChanged?.(); }} />
      <LabelPrintDialog open={!!labelData} onClose={() => setLabelData(null)} labels={labelData || []} title="設備ラベル" />

      {/* 2026-10-23 · 廃棄（理由必須・履歴に残る） */}
      <Dialog open={acceptOpen} onOpenChange={(o) => !o && setAcceptOpen(false)}>
        <DialogContent className="max-w-md" data-testid="eq-acceptance-dialog">
          <DialogHeader><DialogTitle>受入確認</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-600">
              受け取った日・確認した人・メモを残します。完了報告書の新しい版にも記録されます。
            </p>
            <div>
              <Label className="text-xs">受入日</Label>
              <Input type="date" value={acceptForm.accepted_on} className="mt-1"
                onChange={(e) => setAcceptForm((f) => ({ ...f, accepted_on: e.target.value }))}
                data-testid="eq-acceptance-date" />
            </div>
            <div>
              <Label className="text-xs">確認者</Label>
              <Input value={acceptForm.accepted_by_name} className="mt-1"
                placeholder="未入力なら自分の名前で記録します"
                onChange={(e) => setAcceptForm((f) => ({ ...f, accepted_by_name: e.target.value }))}
                data-testid="eq-acceptance-name" />
            </div>
            <div>
              <Label className="text-xs">メモ</Label>
              <Input value={acceptForm.memo} className="mt-1"
                onChange={(e) => setAcceptForm((f) => ({ ...f, memo: e.target.value }))}
                data-testid="eq-acceptance-memo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptOpen(false)}>やめる</Button>
            <Button onClick={submitAcceptance} data-testid="eq-acceptance-submit">記録する</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disposeOpen} onOpenChange={(o) => !o && setDisposeOpen(false)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="eq-dispose-dialog">
          <DialogHeader><DialogTitle>廃棄する</DialogTitle></DialogHeader>
          <p className="text-[12px] text-zinc-600">
            現物を捨てた・使えなくなった設備です。履歴には残り、番号も残ります。
            登録そのものが間違いだった場合は「削除（登録ミス）」を使ってください。
          </p>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">廃棄の理由（必須）</Label>
            <Input value={disposeReason} onChange={(e) => setDisposeReason(e.target.value)}
              className="h-10 mt-1" placeholder="例: 主軸の破損で修理不能"
              data-testid="eq-dispose-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisposeOpen(false)}>{t('mfg.common.cancel')}</Button>
            <Button onClick={dispose} disabled={acting || !disposeReason.trim()}
              className="bg-rose-700 hover:bg-rose-800" data-testid="eq-dispose-confirm">
              廃棄する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-10-23 · 削除（登録ミス）— 管理者のみ・理由必須・元に戻せる */}
      <Dialog open={delOpen} onOpenChange={(o) => !o && setDelOpen(false)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="eq-delete-dialog">
          <DialogHeader><DialogTitle>削除（登録ミス）</DialogTitle></DialogHeader>
          <p className="text-[12px] text-zinc-600">
            間違って登録した設備を、一覧・件数・検索・選択肢から外します。
            番号は欠番のままで、使い回しません。設定 → データ点検 →「削除した設備」から元に戻せます。
          </p>
          {(eq?.delete_blockers || []).length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
              data-testid="eq-delete-blockers">
              <p className="text-[12px] font-bold text-amber-900">
                この設備は使われているので削除できません。現物を捨てた場合は「廃棄」を使ってください。
              </p>
              <ul className="mt-0.5 list-disc pl-5 text-[11px] text-amber-900">
                {eq.delete_blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
              {isAdmin && (
                <label className="mt-2 flex items-start gap-2 text-[12px] font-bold text-amber-900">
                  <input type="checkbox" checked={delForce} className="mt-0.5 h-4 w-4"
                    onChange={(e) => setDelForce(e.target.checked)}
                    data-testid="eq-delete-force" />
                  <span>
                    履歴があっても削除する（管理者）
                    <span className="block font-normal">
                      履歴とリンクはそのまま残ります。参照している完了報告書などには
                      「{eq.asset_no}（削除済み）」と出ます。番号は欠番のままです。
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-zinc-700">削除の理由（必須）</Label>
            <Input value={delReason} onChange={(e) => setDelReason(e.target.value)}
              className="h-10 mt-1" placeholder="例: 同じ設備を二重に登録したため"
              data-testid="eq-delete-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>{t('mfg.common.cancel')}</Button>
            <Button onClick={softDelete}
              disabled={acting || !delReason.trim()
                || ((eq?.delete_blockers || []).length > 0 && !(isAdmin && delForce))}
              className="bg-rose-700 hover:bg-rose-800" data-testid="eq-delete-confirm">
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
